import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Chart } from "chart.js/auto";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ITplMottagningsplatsLeveransplanHandelssortiment, ITplMottagningsplatsLeveransplanHandelssortimentData } from "../../../models/dtos";
import { TaktChartLegendComponent } from "./takt-chart-legend/takt-chart-legend.component";

@Component({
    selector: "rask-takt-chart",
    templateUrl: "./takt-chart.component.html",
    styleUrls: ["./takt-chart.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [TaktChartLegendComponent]
})
export class TaktChartComponent implements OnInit, AfterViewInit {
    @ViewChild("canvas") canvas: ElementRef;
    chart: any;
    chartData: ITplMottagningsplatsLeveransplanHandelssortimentData[] = [];
    @Input() data: ITplMottagningsplatsLeveransplanHandelssortiment;
    dataset = [];
    height: number;
    labels = [];
    points: SafeHtml;

    private readonly sanitizer = inject(DomSanitizer);

    ngAfterViewInit() {
        if (!ObjectUtils.hasValue(this.chartData)) {
            return;
        }

        this.chart = this.getChartConfiguration();
        this.chart.render();
    }

    ngOnInit(): void {
        if (!ObjectUtils.hasValue(this.data)) {
            return;
        }

        this.chartData = this.chartData.concat(this.data.produktionsomraden, this.data.leverantorsorder, this.data.terminaler);

        this.height = this.chartData.length * 15 > 0 ? this.chartData.length * 15 : 15;
        this.prepData();
    }

    private getChartConfiguration() {
        const defaultFontColor = "rgba(0,0,0,1)";
        const defaultBackgroundColor = "rgba(255,255,255,1)";
        return new Chart(this.canvas.nativeElement, {
            type: "bar",
            data: {
                labels: this.labels,
                datasets: this.dataset
            },
            options: {
                indexAxis: "y",
                maintainAspectRatio: true,
                layout: {
                    padding: {
                        left: 150
                    }
                },
                scales: {
                    x: {
                        grid: {
                            display: true
                        },
                        ticks: {
                            display: true,
                            font: {
                                size:  14
                            }
                        },
                        stacked: true,
                        beginAtZero: true
                    },
                    y: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            font: {
                                size:  14
                            }
                        },
                        stacked: true
                    }
                },
                plugins: {
                    tooltip: {
                        position: "nearest",
                        displayColors: true,
                        backgroundColor: defaultBackgroundColor,
                        borderColor: defaultFontColor,
                        cornerRadius: 3,
                        padding: 12,
                        bodyColor: defaultFontColor,
                        titleColor: defaultFontColor,
                        footerColor: defaultFontColor,
                        footerFont: {
                            weight: "bold"
                        },
                        footerMarginTop: 10,
                        borderWidth: 1,
                        bodyFont: {
                            weight: "normal"
                        },
                        intersect: true,
                        mode: "nearest"
                    },
                    legend: {
                        display: false
                    },
                    title: {
                        display: false,
                        text: ""
                    }
                }
            }
        });
    }

    private prepData() {
        const stackName = "stack";

        const toLabel = (data: ITplMottagningsplatsLeveransplanHandelssortimentData): string => {
            if (!data.takt) {
                return data.forsorjningskalla;
            }
            return `${data.forsorjningskalla} (${data.takt}%)`;
        };

        this.labels = this.chartData.map(toLabel);

        const planerat = this.chartData.map(x => x.planeradVolym);
        const transporterat = this.chartData.map(x => x.transporteradVolym);
        const vaglager = this.chartData.map(x => x.destineradVaglagerVolym);

        const dataset = [
            {
                label: "Planerat",
                backgroundColor: "#f68d38",
                data: planerat,
                barThickness: "flex"
            },
            {
                label: "Transporterat",
                stack: stackName,
                backgroundColor: "#8c8c8c",
                data: transporterat,
                barThickness: "flex"
            },
            {
                label: "Väglager",
                stack: stackName,
                backgroundColor: "#5aa0fe",
                data: vaglager,
                barThickness: "flex"
            }
        ];

        this.dataset = dataset;
    }
}
